import { useMemo, useState } from "react";
import { sucheEinfach, sucheWeine, weineVonWinzer } from "../dienste/katalog.js";
import { t } from "../i18n/index.js";

/* ================================================================
   Übernahme aus dem Katalog — sitzt oben in den Anlege-Formularen.

   Erscheint nur im Cloud-Modus, nur beim Neuanlegen und nur, wenn der
   Katalog überhaupt etwas enthält. Beim Bearbeiten eines bestehenden
   Objekts wäre sie fehl am Platz.

   Beim Wein führen zwei Wege zum Ziel: entweder direkt über den
   Weinnamen — oder über den Winzer. Wer den Winzer wählt, sieht
   dessen Weine im Katalog und kann einen davon übernehmen oder einen
   eigenen anlegen; der Winzer wandert dann samt Region schon einmal
   ins eigene Kellerbuch und steht im Formular bereit.
   ================================================================ */

function KatalogUebernahme({ katalog, art, onUebernehmen, onWinzerNutzen = null }) {
  const [text, setText] = useState("");
  /* Nur beim Wein: der aus der Trefferliste gewählte Winzer. */
  const [winzer, setWinzer] = useState(null);

  const regionById = useMemo(
    () => new Map((katalog?.regionen || []).map((r) => [r.id, r])), [katalog]);

  const winzerNeben = (w) => {
    const r = w.regionId ? regionById.get(w.regionId) : null;
    return [r && r.name, w.ort].filter(Boolean).join(" · ");
  };

  const treffer = useMemo(() => {
    if (!katalog) return [];
    if (art === "wein") return sucheWeine(katalog, text);
    if (art === "winzer") return sucheEinfach(katalog.winzer, text, 8, winzerNeben);
    return sucheEinfach(katalog.regionen, text, 8, (r) => r.land || "");
  }, [katalog, art, text, regionById]);

  /* Beim Wein zusätzlich nach Winzern suchen — dieselbe Eingabe. */
  const winzerTreffer = useMemo(() => {
    if (!katalog || art !== "wein" || !onWinzerNutzen) return [];
    return sucheEinfach(katalog.winzer, text, 5, winzerNeben);
  }, [katalog, art, text, regionById, onWinzerNutzen]);

  const winzerWeine = useMemo(
    () => (winzer ? weineVonWinzer(katalog, winzer.id) : []), [katalog, winzer]);

  if (!katalog) return null;
  const bestand = art === "wein" ? katalog.weine.length
    : art === "winzer" ? katalog.winzer.length : katalog.regionen.length;
  if (bestand === 0) return null;

  const label = art === "wein" ? "Wein" : art === "winzer" ? "Winzer" : "Region";

  const trefferKnopf = (objekt, neben, onClick, schluessel) => (
    <li key={schluessel || objekt.id}>
      <button className="wl-kat-treffer" onClick={onClick}>
        <span className="wl-kat-name">{objekt.name}</span>
        {neben && <span className="wl-kat-neben">{neben}</span>}
      </button>
    </li>
  );

  /* ── Zweiter Schritt: die Weine des gewählten Winzers ── */
  if (winzer) {
    return (
      <div className="wl-kat">
        <div className="wl-kat-kopf">
          <span className="wl-kat-titel">{winzer.name}</span>
          <button className="wl-kat-zurueck" onClick={() => setWinzer(null)}>‹ {t("andere Suche")}</button>
        </div>
        {winzerNeben(winzer) && <p className="wl-kat-neben">{winzerNeben(winzer)}</p>}
        {winzerWeine.length === 0
          ? <p className="wl-kat-leer">{t("Für diesen Winzer sind noch keine Weine im Katalog.")}</p>
          : (
            <>
              <p className="wl-kat-leer">
                {t.plural(winzerWeine.length,
                  "Ein Wein dieses Winzers im Katalog:",
                  "{n} Weine dieses Winzers im Katalog:")}
              </p>
              <ul className="wl-kat-liste">
                {winzerWeine.map((w) => trefferKnopf(w,
                  [w.appellation, w.typ].filter(Boolean).join(" · "),
                  () => onUebernehmen(w)))}
              </ul>
            </>
          )}
        <div className="wl-kat-trenner"><span>oder</span></div>
        <button className="wl-kat-eigener" onClick={() => onWinzerNutzen(winzer)}>
          {t("Eigenen Wein dieses Winzers anlegen")}
        </button>
      </div>
    );
  }

  /* ── Erster Schritt: Suche ── */
  return (
    <div className="wl-kat">
      <div className="wl-kat-kopf">
        <span className="wl-kat-titel">Aus dem Katalog übernehmen</span>
        <span className="wl-kat-anzahl">{bestand} {label === "Region" ? "Regionen" : label + "e"} verfügbar</span>
      </div>
      <input className="wl-eingabe" value={text} onChange={(e) => setText(e.target.value)}
        placeholder={art === "wein" && onWinzerNutzen
          ? "Wein oder Winzer im Katalog suchen …"
          : `${label} im Katalog suchen …`} />
      {text.trim().length >= 2 && treffer.length === 0 && winzerTreffer.length === 0 && (
        <p className="wl-kat-leer">Nichts gefunden — leg ihn unten einfach selbst an.</p>
      )}
      {treffer.length > 0 && (
        <>
          {winzerTreffer.length > 0 && <p className="wl-kat-gruppe">{t("Weine")}</p>}
          <ul className="wl-kat-liste">
            {treffer.map(({ objekt, neben }) => trefferKnopf(objekt, neben, () => onUebernehmen(objekt)))}
          </ul>
        </>
      )}
      {winzerTreffer.length > 0 && (
        <>
          <p className="wl-kat-gruppe">{t("Winzer")}</p>
          <ul className="wl-kat-liste">
            {winzerTreffer.map(({ objekt, neben }) => trefferKnopf(objekt, neben,
              () => setWinzer(objekt), "wz" + objekt.id))}
          </ul>
        </>
      )}
      <div className="wl-kat-trenner"><span>oder selbst anlegen</span></div>
    </div>
  );
}

export { KatalogUebernahme };
